<script setup lang="ts">
import { nextTick, ref, shallowRef, watch } from 'vue';
import { useFloatingPanel } from '../Popover/useFloatingPanel';
import NavBarMenuItem from './NavBarMenuItem.vue';
import type { NavBarNode } from './types';

const props = defineProps<{
  /** The parent node whose children we're rendering. */
  parent: NavBarNode;
  /** Whether this popover is currently open. */
  open: boolean;
  /** Whether this is the top-level popover anchored under a root button. */
  isRoot: boolean;
  /** The id of the currently focused descendant (or null). */
  focusedId: string | null;
  /** The chain of currently open child popovers (sub-sub etc), as ids. */
  openChildIds: readonly string[];
}>();

const emit = defineEmits<{
  activateChild: [child: NavBarNode, event: PointerEvent | KeyboardEvent];
  hoverChild: [child: NavBarNode];
}>();

const popoverRef = ref<HTMLElement | null>(null);

/**
 * The entry this menu opens from: its parent element, the root button's
 * wrapper or the row of the parent item. Positioned in JS like NxPopover, not
 * with CSS anchor positioning, which Safari ignores or misplaces (I-3820).
 * Set only while open: closed menus stay mounted (v-show), and an anchor
 * would keep Floating UI's scroll and resize observers running for each.
 */
const anchorRef = shallowRef<HTMLElement | null>(null);

/** A nested menu overlaps its row by the menu's own padding (4px). */
const NESTED_OVERLAP = -4;
const { floatingStyles } = useFloatingPanel(anchorRef, popoverRef, {
  open: () => props.open,
  placement: () => (props.isRoot ? 'bottom-start' : 'right-start'),
  offset: () => (props.isRoot ? 0 : NESTED_OVERLAP),
});

// Dual-control: v-show on the popover element is the fallback for browsers
// that ignore the `popover` attribute entirely. showPopover/hidePopover
// handle top-layer promotion when the API is available. Both must stay in
// sync — do not remove one without the other.
// Show / hide via the native popover API (best-effort; gracefully no-ops if unsupported).
watch(
  () => props.open,
  (isOpen) => {
    nextTick(() => {
      const el = popoverRef.value;
      anchorRef.value = isOpen ? (el?.parentElement ?? null) : null;
      if (!el || !('showPopover' in el)) return;
      if (isOpen) {
        try {
          el.showPopover();
        } catch {
          /* already open */
        }
      } else {
        try {
          el.hidePopover();
        } catch {
          /* already closed */
        }
      }
    });
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-show="open"
    ref="popoverRef"
    popover="manual"
    role="menu"
    :aria-label="parent.text"
    :style="floatingStyles"
    class="menubar-content"
  >
    <template v-for="child in parent.children" :key="child.id">
      <div class="menubar-row-anchor">
        <NavBarMenuItem
          :node="child"
          :highlighted="focusedId === child.id"
          :open="openChildIds.includes(child.id)"
          @activate="(e) => emit('activateChild', child, e)"
          @pointer-enter="emit('hoverChild', child)"
        />
        <NavBarSubMenu
          v-if="child.children.length > 0"
          :parent="child"
          :is-root="false"
          :open="openChildIds.includes(child.id)"
          :focused-id="focusedId"
          :open-child-ids="openChildIds"
          @activate-child="(c, e) => emit('activateChild', c, e)"
          @hover-child="(c) => emit('hoverChild', c)"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.menubar-row-anchor {
  position: relative;
}

/* Placed by useFloatingPanel: under the root entry, or beside the parent row,
   flipping and shifting to stay in the viewport. */
.menubar-content {
  position: fixed;
  inset: auto;
  margin: 0;
  padding: var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  background-color: var(--nxt-surface-raised-bg);
  border-radius: var(--nxt-radius-sm);
  box-shadow: var(--nxt-shadow-md);
  min-width: var(--nxt-panel-width-xs);
  overflow: visible;
  display: flex;
  flex-direction: column;
}
</style>
